import { useState, useMemo, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { Accordion } from '../../components/common/Accordion';
import { EstimateCard } from '../../components/common/EstimateCard';
import { StepHeader } from '../../components/common/StepHeader';
import { AdditionalDetails } from '../../components/wizard/AdditionalDetails';
import { CustomServiceRequest } from '../../components/wizard/CustomServiceRequest';
import { useWizardFlow } from '../../hooks/useWizardFlow';
import { 
  PLUMBING_QUESTIONS, 
  PLUMBING_SERVICE_CATEGORIES 
} from './plumbingData';
import { calculatePlumbingEstimate } from './plumbingPricing';
import { MAX_ADDITIONAL_DETAILS } from '../../data/constants';

/**
 * Check if a question should be shown based on selected services
 */
const shouldShowQuestion = (question, answers) => {
  // Check showIfAnyService condition
  if (question.showIfAnyService) {
    const selectedServices = answers.plumbing_services || [];
    const hasMatchingService = question.showIfAnyService.some(svc => 
      selectedServices.includes(svc)
    );
    if (!hasMatchingService) return false;
  }
  return true;
};

/**
 * PlumbingFlow Component
 * Handles the complete Plumbing quote wizard flow using modular architecture
 * Now uses shared useWizardFlow hook for consistent navigation behavior
 */
export const PlumbingFlow = ({ 
  answers, 
  setAnswers,
  additionalDetails,
  setAdditionalDetails,
  unlistedServiceDescription = '',
  setUnlistedServiceDescription = () => {},
  onComplete,
  onBack 
}) => {
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  // Get selected services
  const selectedServices = answers.plumbing_services || [];
  
  // Use shared wizard flow hook with auto-advance on radio
  const {
    currentQuestion,
    currentQuestionIndex,
    isLastQuestion,
    canProceed: hookCanProceed,
    goNext,
    goBack,
    handleRadioAnswer,
    handleCheckboxChange
  } = useWizardFlow({
    questions: PLUMBING_QUESTIONS,
    answers,
    setAnswers,
    onComplete: () => {
      // Complete the flow
      const finalAnswers = {
        ...answers,
        ...(additionalDetails.trim() && { additional_details: additionalDetails.trim() })
      };
      const estimatedPrice = calculatePlumbingEstimate(selectedServices, answers);
      onComplete(finalAnswers, estimatedPrice);
    },
    onBack,
    shouldShowQuestion,
    autoAdvanceOnRadio: true,  // Auto-advance on radio selections
    autoAdvanceDelay: 300,
    initialQuestionIndex: 0
  });
  
  // Calculate price estimate
  const estimatedPrice = useMemo(() => {
    return calculatePlumbingEstimate(selectedServices, answers);
  }, [selectedServices, answers]);
  
  // Toggle category expansion
  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);
  
  // Handle service selection toggle (for collapsible_categories)
  const handleServiceToggle = useCallback((serviceValue, checked) => {
    const current = answers.plumbing_services || [];
    const newServices = checked
      ? [...current, serviceValue]
      : current.filter(v => v !== serviceValue);
    setAnswers(prev => ({ ...prev, plumbing_services: newServices }));
  }, [answers, setAnswers]);
  
  // Clear all services
  const handleClearServices = useCallback(() => {
    setAnswers(prev => ({ ...prev, plumbing_services: [] }));
  }, [setAnswers]);
  
  // Custom canProceed for collapsible_categories type
  const canProceed = useMemo(() => {
    if (!currentQuestion) return false;
    
    if (currentQuestion.type === 'collapsible_categories') {
      return selectedServices.length > 0;
    }
    
    return hookCanProceed;
  }, [currentQuestion, selectedServices, hookCanProceed]);
  
  // Handle back navigation
  const handleBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      goBack();
    } else {
      onBack();
    }
  }, [currentQuestionIndex, goBack, onBack]);
  
  if (!currentQuestion) {
    return <div>Loading...</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Question Header */}
      <StepHeader
        title={currentQuestion.question}
        subtitle={currentQuestion.subtitle}
        onBack={handleBack}
        showBack={true}
      />
      
      {/* Question Content */}
      <div className="space-y-4">
        {/* Collapsible Categories (Service Selection) */}
        {currentQuestion.type === 'collapsible_categories' && (
          <>
            <div className="space-y-3">
              {PLUMBING_SERVICE_CATEGORIES.map((category) => {
                const isExpanded = expandedCategories.includes(category.id);
                const selectedInCategory = category.services.filter(s => 
                  selectedServices.includes(s.value)
                ).length;
                
                return (
                  <Accordion
                    key={category.id}
                    id={category.id}
                    title={category.name}
                    emoji={category.emoji}
                    badge={selectedInCategory > 0 ? `${selectedInCategory} selected` : null}
                    isExpanded={isExpanded}
                    onToggle={() => toggleCategory(category.id)}
                    testIdPrefix="category"
                  >
                    {category.services.map((service) => {
                      const isChecked = selectedServices.includes(service.value);
                      return (
                        <label
                          key={service.value}
                          className={`flex items-center gap-4 p-4 pl-12 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
                            isChecked ? 'bg-[#0FA3B1]/5' : ''
                          }`}
                          data-testid={`service-${service.value}`}
                        >
                          <Checkbox 
                            checked={isChecked}
                            onCheckedChange={(checked) => handleServiceToggle(service.value, checked)}
                            className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                          />
                          <div className="flex-1">
                            <span className="text-[#1F2933]">{service.label}</span>
                            {service.price && (
                              <span className="text-xs text-[#1F2933]/50 ml-2">
                                ${service.price.low}–${service.price.high}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </Accordion>
                );
              })}
            </div>
            
            {/* Selected count */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-[#1F2933]/60">
                {selectedServices.length} service(s) selected
              </span>
              {selectedServices.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearServices}
                  className="text-sm text-red-500 hover:text-red-600"
                >
                  Clear all
                </button>
              )}
            </div>
          </>
        )}
        
        {/* Radio Question */}
        {currentQuestion.type === 'radio' && (
          <RadioGroup
            value={answers[currentQuestion.id] || ""}
            onValueChange={handleRadioAnswer}
            className="space-y-3"
          >
            {currentQuestion.options.map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                  answers[currentQuestion.id] === option.value
                    ? "border-[#0FA3B1] bg-[#0FA3B1]/5"
                    : "border-gray-200"
                }`}
                data-testid={`option-${option.value}`}
              >
                <RadioGroupItem
                  value={option.value}
                  className="border-2 border-gray-300 text-[#0FA3B1]"
                />
                <span className="text-[#1F2933] flex-1">{option.label}</span>
              </label>
            ))}
          </RadioGroup>
        )}
        
        {/* Checkbox Question */}
        {currentQuestion.type === 'checkbox' && (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const isChecked = (answers[currentQuestion.id] || []).includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                    isChecked
                      ? "border-[#0FA3B1] bg-[#0FA3B1]/5"
                      : "border-gray-200"
                  }`}
                  data-testid={`option-${option.value}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => handleCheckboxChange(option.value, checked)}
                    className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                  />
                  <span className="text-[#1F2933] flex-1">{option.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Additional Details */}
      <div className="mt-6">
        <AdditionalDetails
          value={additionalDetails}
          onChange={setAdditionalDetails}
          placeholder="Share any specific requirements, measurements, preferences, or unique project details..."
          maxLength={MAX_ADDITIONAL_DETAILS}
          minHeight="100px"
        />
      </div>
      
      {/* "Can't find what you need?" — only on first question (service selection) */}
      {currentQuestionIndex === 0 && (
        <CustomServiceRequest
          value={unlistedServiceDescription}
          onChange={setUnlistedServiceDescription}
        />
      )}
      
      {/* Running Estimate */}
      <EstimateCard priceRange={estimatedPrice} isCustomRequest={!estimatedPrice.hasValidPrice && !!unlistedServiceDescription.trim()} />
      
      {/* Navigation */}
      <Button 
        onClick={goNext}
        disabled={!canProceed && !(currentQuestionIndex === 0 && unlistedServiceDescription.trim())}
        className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
        data-testid={isLastQuestion ? "plumbing-complete" : "plumbing-next"}
      >
        {isLastQuestion ? "Get My Quote" : "Continue"} <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
};

export default PlumbingFlow;
